import type * as Sentry from "@sentry/react";

export type SentryTransactionEvent = Parameters<NonNullable<Sentry.BrowserOptions["beforeSendTransaction"]>>[0];

const INVITE_CODE_PATH = /\/register\/[^/?#\s]+/g;
const OAUTH_CALLBACK_PATHS = ["/oauth-redirect", "/settings/integrations/ramp/callback"];
const OAUTH_CALLBACK_QUERY_VALUES = /([?&](?:code|state)=)[^&#\s]*/g;
const CHANGE_PASSWORD_ID_VALUE = /(changePasswordId=)[^&#\s]*/g;
const TOKEN_QUERY_VALUE = /([?&]token=)[^&#\s]*/g;
const PAY_RETURN_PATH = /\/pay\/return\/[^/?#\s]+/g;
const PAY_TOKEN_PATH = /\/pay\/(?!return(?:[/?#\s]|$))[^/?#\s]+/g;
const PAY_RETURN_SECRET_VALUE = /([?&]r=)[^&#\s]*/g;
const QUERY_STRING_KEY = "query_string";
const RETURN_SECRET_KEY = "r";
const QUOTED_PAY_VALUE = /("(?:token|secret|attemptId)"\s*:\s*")[^"]*(")/g;
const ESCAPED_QUOTED_PAY_VALUE = /(\\"(?:token|secret|attemptId)\\"\s*:\s*\\")[^"\\]*(\\")/g;
const SENSITIVE_KEYS = new Set(["changePasswordId", "password", "token", "secret", "attemptId"]);
const REDACTED = "[REDACTED]";
const MAX_DEPTH = 10;
const MIN_REGISTERED_VALUE_LENGTH = 8;
const MAX_REGISTERED_VALUES = 20;
const registeredValues: string[] = [];

export const registerRedactedValue = (value: string): void => {
	if (value.length < MIN_REGISTERED_VALUE_LENGTH || registeredValues.includes(value)) return;
	registeredValues.push(value);
	if (registeredValues.length > MAX_REGISTERED_VALUES) registeredValues.shift();
};

const redactInviteCode = (text: string): string => text.replace(INVITE_CODE_PATH, "/register/:inviteCode");

const redactOAuthCallbackQuery = (text: string): string =>
	OAUTH_CALLBACK_PATHS.some((path) => text.includes(path))
		? text.replace(OAUTH_CALLBACK_QUERY_VALUES, `$1${REDACTED}`)
		: text;

const redactChangePasswordId = (text: string): string => text.replace(CHANGE_PASSWORD_ID_VALUE, `$1${REDACTED}`);

const redactTokenQuery = (text: string): string => text.replace(TOKEN_QUERY_VALUE, `$1${REDACTED}`);

const redactPayPaths = (text: string): string => {
	if (!text.includes("/pay/")) return text;
	const withoutReturnSecret = text.includes("/pay/return/")
		? text.replace(PAY_RETURN_SECRET_VALUE, `$1${REDACTED}`).replace(PAY_RETURN_PATH, "/pay/return/:attemptId")
		: text;
	return withoutReturnSecret.replace(PAY_TOKEN_PATH, "/pay/:token");
};

const redactQuotedPayValues = (text: string): string =>
	text.replace(QUOTED_PAY_VALUE, `$1${REDACTED}$2`).replace(ESCAPED_QUOTED_PAY_VALUE, `$1${REDACTED}$2`);

const redactRegisteredValues = (text: string): string =>
	registeredValues.reduce((redacted, value) => redacted.split(value).join(REDACTED), text);

const redactReturnSecretQueryString = (queryString: string): string =>
	queryString.replace(/(^|[?&])(r=)[^&#]*/g, `$1$2${REDACTED}`);

const redactReturnSecretPairs = (pairs: unknown[]): void => {
	pairs.forEach((pair, index) => {
		if (Array.isArray(pair) && pair[0] === RETURN_SECRET_KEY) pairs[index] = [RETURN_SECRET_KEY, REDACTED];
	});
};

export const redactText = (text: string): string =>
	redactRegisteredValues(
		redactQuotedPayValues(
			redactPayPaths(redactTokenQuery(redactChangePasswordId(redactOAuthCallbackQuery(redactInviteCode(text)))))
		)
	);

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
	if (value === null || typeof value !== "object") return false;
	const prototype = Object.getPrototypeOf(value);
	return prototype === Object.prototype || prototype === null;
};

const redactInPlace = (value: unknown, depth: number): void => {
	if (depth >= MAX_DEPTH) return;
	if (Array.isArray(value)) {
		value.forEach((item, index) => {
			if (typeof item === "string") value[index] = redactText(item);
			else redactInPlace(item, depth + 1);
		});
		return;
	}
	if (!isPlainObject(value)) return;
	for (const [key, item] of Object.entries(value)) {
		if (SENSITIVE_KEYS.has(key)) value[key] = REDACTED;
		else if (key === QUERY_STRING_KEY && typeof item === "string")
			value[key] = redactText(redactReturnSecretQueryString(item));
		else if (key === QUERY_STRING_KEY && Array.isArray(item)) redactReturnSecretPairs(item);
		else if (key === QUERY_STRING_KEY && isPlainObject(item) && RETURN_SECRET_KEY in item)
			item[RETURN_SECRET_KEY] = REDACTED;
		else if (typeof item === "string") value[key] = redactText(item);
		else redactInPlace(item, depth + 1);
	}
};

export const redactSentryErrorEvent = (event: Sentry.ErrorEvent): Sentry.ErrorEvent => {
	redactInPlace(event, 0);
	return event;
};

export const redactReplayRecordingEvent = <T extends object>(event: T): T => {
	redactInPlace(event, 0);
	return event;
};

export const redactSentryTransaction = (event: SentryTransactionEvent): SentryTransactionEvent => {
	redactInPlace(event, 0);
	return event;
};

const redactSentryBreadcrumb = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb => {
	redactInPlace(breadcrumb, 0);
	return breadcrumb;
};

export default redactSentryBreadcrumb;
